import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import Card from '../UI/Card';
import ToggleSwitch from './ToggleSwitch';
import { investigationsApi } from '../../api/investigations';

const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T') + 'Z');
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const statusLabel =(row) => {
  switch (row.status) {
    case 'queued': return 'Queued';
    case 'running': return 'Running';
    case 'completed': return 'Done';
    case 'failed': return 'Failed';
    case 'skipped':
      if (row.reason === 'no_llm') return 'Skipped: no AI model configured';
      if (row.reason === 'expired') return 'Skipped: expired';
      if (row.reason === 'disabled') return 'Skipped: AI switched off';
      return 'Skipped';
    default: return row.status;
  }
};

const AutoInvestigationCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const saved = useRef(false);

  useEffect(() => {
    let alive = true;
    investigationsApi.getSettings()
      .then((s) => {
        // A late response must not overwrite a value the user just saved.
        if (!alive || saved.current) return;
        setEnabled(!!s.enabled);
        setLoaded(true);
      })
      .catch((err) => {
        if (!alive || saved.current) return;
        setLoadError(err?.response?.status === 403
          ? 'Only administrators can change this setting.'
          : 'Could not load the current setting.');
      });
    investigationsApi.list(5).then((r) => alive && setRows(r || [])).catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async (next) => {
    setBusy(true);
    setError(null);
    try {
      const result = await investigationsApi.setEnabled(next);
      saved.current = true;
      setEnabled(!!result.enabled);
      setLoaded(true);
      setLoadError(null);
    } catch (err) {
      setError(errorText(err, 'Could not save this setting.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-orange-500/70" />
          <h3 className="text-base font-semibold text-gray-100">AI triage and investigation</h3>
        </div>
        <p className="text-sm text-gray-400 mt-0.5">
          When a critical or high alert or finding appears, the AI gives it a quick verdict and, once per host per hour,
          runs a full investigation and sends you a summary with recommendations. It never takes any action itself.
          Needs an AI model configured under Integrations.
        </p>
      </Card.Header>
      <Card.Body className="space-y-4">
        {loadError && !loaded && (
          <div role="status" aria-live="polite" className="px-4 py-3 rounded-lg border text-sm bg-yellow-500/10 border-yellow-500/30 text-yellow-400">
            {loadError}
          </div>
        )}

        {error && (
          <div role="status" aria-live="polite" className="px-4 py-3 rounded-lg border text-sm bg-red-500/10 border-red-500/30 text-red-400">
            {error}
          </div>
        )}

        <ToggleSwitch
          label="Automatically triage and investigate critical and high alerts"
          description="Every critical or high alert gets a quick verdict; one full investigation per host per hour."
          enabled={enabled}
          disabled={busy || !loaded}
          onChange={handleToggle}
        />

        {rows.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-400 mb-1">Recent investigations</p>
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id} className="text-xs text-gray-500 flex justify-between gap-3">
                  <span className="truncate">{row.hostname || row.server_id}</span>
                  <span className="text-gray-600 whitespace-nowrap">{formatTime(row.created_at)}</span>
                  <span className={row.status === 'failed' ? 'text-red-400' : row.status === 'completed' ? 'text-emerald-400' : 'text-gray-500'}>
                    {statusLabel(row)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AutoInvestigationCard;
